import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useRef, useEffect } from 'preact/hooks';
import { useForm } from 'react-hook-form';
import { X, FileText, AlertTriangle } from 'lucide-preact';
import { audienceApi } from '@/api/audience';
import { parseValidationErrors, setFormValidationErrors } from '@/utils/errorHandling';
import { TextBox, FileUpload } from '@/components';
const VALIDATION_CONSTANTS = {
    NAME_MAX_LENGTH: 100,
    DESCRIPTION_MAX_LENGTH: 1000,
};
export function CampaignListModal({ isOpen, onClose, campaignId, clientCompanyId, selectedWorkspaceId, onCampaignListSaved }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [businessRuleErrors, setBusinessRuleErrors] = useState([]);
    const [showAllErrors, setShowAllErrors] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState();
    const cleanupRef = useRef(null);
    const assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL;
    const sampleFileUrl = `${assetsBaseUrl}/assets/file-upload-sample/sample-file-upload.csv`;
    const { register, handleSubmit, formState: { errors, isValid }, reset, clearErrors, setError } = useForm({
        mode: 'onChange',
        defaultValues: {
            Name: '',
            Description: ''
        }
    });
    // Cleanup on unmount or when modal closes
    useEffect(() => {
        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, []);
    const handleClose = () => {
        // Cancel any in-flight requests
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }
        reset();
        setBusinessRuleErrors([]);
        setShowAllErrors(false);
        setSelectedFile(null);
        setFileError(undefined);
        setIsSubmitting(false);
        onClose();
    };
    const handleFileChange = (file) => {
        setSelectedFile(file);
        if (file) {
            setFileError(undefined);
            clearErrors('root.file');
        }
    };
    const handleFileError = (error) => {
        setFileError(error);
        setError('root.file', { type: 'manual', message: error });
    };
    const onSubmit = async (data) => {
        if (!selectedFile) {
            setError('root.file', { type: 'manual', message: 'Please select a file to upload.' });
            return;
        }
        setIsSubmitting(true);
        let cancelled = false;
        // Store cleanup function
        cleanupRef.current = () => {
            cancelled = true;
        };
        try {
            const createDto = {
                CampaignId: campaignId,
                ClientCompanyId: clientCompanyId,
                WorkspaceId: selectedWorkspaceId,
                Name: data.Name.trim(),
                Description: data.Description?.trim() || undefined
            };
            const response = await audienceApi.create(clientCompanyId, selectedWorkspaceId, createDto, selectedFile);
            if (cancelled)
                return;
            if (response.error) {
                // Global handler shows toasts for 422 validation errors
                if (response.status === 422) {
                    // Parse validation errors for custom display
                    const validationErrors = parseValidationErrors(response);
                    setBusinessRuleErrors(validationErrors);
                    // Set form-level errors for any field-specific errors
                    setFormValidationErrors(response, setError);
                    setError('root.submit', { type: 'manual', message: 'Some rows failed validation.' });
                }
                else {
                    // For non-422 errors, set root error
                    setError('root.submit', { type: 'manual', message: response.error });
                }
                return;
            }
            if (response.data) {
                onCampaignListSaved(response.data);
                handleClose();
            }
        }
        catch (error) {
            if (!cancelled) {
                setError('root.submit', { type: 'manual', message: 'Failed to create audience. Please try again.' });
            }
        }
        finally {
            if (!cancelled) {
                setIsSubmitting(false);
                cleanupRef.current = null;
            }
        }
    };
    if (!isOpen)
        return null;
    const canSubmit = isValid && selectedFile && !isSubmitting;
    return (_jsx("div", { className: "fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4", children: _jsxs("div", { className: "bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-600", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "p-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg", children: _jsx(FileText, { className: "w-5 h-5 text-primary-600 dark:text-primary-400" }) }), _jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold text-slate-900 dark:text-white", children: "New Audience" }), _jsx("p", { className: "text-sm text-slate-600 dark:text-slate-300", children: "Upload a CSV file to create a new audience" })] })] }), _jsx("button", { onClick: handleClose, className: "p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsx("div", { className: "flex-1 p-6", children: _jsxs("form", { id: "campaign-list-form", onSubmit: handleSubmit(onSubmit), className: "space-y-6", children: [_jsx(FileUpload, { label: "File Upload", required: true, accept: ".csv,text/csv,application/csv", maxSizeMB: 10, selectedFile: selectedFile, onFileChange: handleFileChange, onError: handleFileError, error: fileError, helperText: "Choose CSV file to upload", sampleFileUrl: sampleFileUrl, sampleFileName: "Sample File Upload File.csv" }), _jsx(TextBox, { label: "Audience Name", required: true, error: errors.Name?.message, showCharCounter: true, maxLength: VALIDATION_CONSTANTS.NAME_MAX_LENGTH, placeholder: "Enter a descriptive name for this audience", ...register('Name', {
                                    required: 'Name is required',
                                    maxLength: { value: VALIDATION_CONSTANTS.NAME_MAX_LENGTH, message: `Name cannot exceed ${VALIDATION_CONSTANTS.NAME_MAX_LENGTH} characters` },
                                    validate: (value) => {
                                        const trimmed = value?.trim();
                                        if (!trimmed)
                                            return 'Name cannot be empty or just whitespace';
                                        return true;
                                    }
                                }) }), _jsx(TextBox, { label: "Description", multiline: true, rows: 3, error: errors.Description?.message, showCharCounter: true, maxLength: VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH, placeholder: "Optional description of this audience", ...register('Description', { maxLength: { value: VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH, message: `Description cannot exceed ${VALIDATION_CONSTANTS.DESCRIPTION_MAX_LENGTH} characters` } }) }), errors.root?.submit && (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg", children: [_jsx(AlertTriangle, { className: "w-4 h-4 text-red-600 dark:text-red-400" }), _jsx("p", { className: "text-sm text-red-600 dark:text-red-400 font-medium", children: errors.root.submit.message })] }), businessRuleErrors.length > 0 && (_jsxs("div", { className: "border border-red-200 dark:border-red-800 rounded-lg bg-white dark:bg-slate-800/50", children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2 border-b border-red-100 dark:border-red-800/60", children: [_jsx("p", { className: "text-xs font-semibold tracking-wide text-red-700 dark:text-red-300 uppercase", children: "Validation Details" }), businessRuleErrors.length > 5 && (_jsx("button", { type: "button", onClick: () => setShowAllErrors(s => !s), className: "text-xs text-red-600 dark:text-red-400 hover:underline", children: showAllErrors ? 'Show less' : `Show all (${businessRuleErrors.length})` }))] }), _jsx("ul", { className: "max-h-56 overflow-auto divide-y divide-red-100 dark:divide-red-800/40 text-sm", children: (showAllErrors ? businessRuleErrors : businessRuleErrors.slice(0, 5)).map((e, i) => (_jsxs("li", { className: "px-3 py-2", children: [_jsx("p", { className: "text-red-700 dark:text-red-300 font-medium leading-snug", children: e.Title || 'Error' }), _jsx("p", { className: "text-red-600 dark:text-red-400 text-xs break-words whitespace-pre-wrap", children: e.Message })] }, i))) }), businessRuleErrors.length > 5 && !showAllErrors && (_jsxs("div", { className: "px-3 py-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 border-t border-red-100 dark:border-red-800/40", children: ["Showing first 5 of ", businessRuleErrors.length, " errors."] }))] }))] }))] }) }), _jsxs("div", { className: "flex items-center justify-end gap-3 p-6 border-t border-slate-200 dark:border-slate-600", children: [_jsx("button", { type: "button", onClick: handleClose, className: "px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors", children: "Cancel" }), _jsx("button", { type: "submit", form: "campaign-list-form", disabled: !canSubmit, className: `px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${canSubmit ? 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 transform hover:scale-105 hover:shadow-lg' : 'bg-slate-300 dark:bg-slate-600 cursor-not-allowed'}`, children: isSubmitting ? 'Creating...' : 'Create Audience' })] })] }) }));
}
